import React, { useEffect, useState } from 'react';
import { useConfirmation } from '../../../../contexts/ConfirmationContext';
import { getEmailCenterConfig } from '../../../../services/emailPlaceholderService';

const DEFAULT_BULK_THRESHOLD = 100;

/**
 * Delivery options: send timing, priority, reply-to/cc/bcc, attachments, send-test,
 * and a mandatory confirmation step (with an extra step above the bulk-send threshold).
 */
const DeliveryOptionsPanel = ({ recipientCount = 0, onSend, onSaveDraft, onSendTest, sending = false }) => {
    const confirm = useConfirmation();
    const [sendMode, setSendMode] = useState('IMMEDIATE'); // IMMEDIATE | SCHEDULED | DRAFT
    const [scheduledAt, setScheduledAt] = useState('');
    const [priority, setPriority] = useState('NORMAL');
    const [replyTo, setReplyTo] = useState('');
    const [cc, setCc] = useState('');
    const [bcc, setBcc] = useState('');
    const [attachments, setAttachments] = useState([]);
    const [bulkThreshold, setBulkThreshold] = useState(DEFAULT_BULK_THRESHOLD);
    const [maxAttachmentMb, setMaxAttachmentMb] = useState(10);

    useEffect(() => {
        getEmailCenterConfig()
            .then((cfg) => {
                if (cfg?.bulkConfirmationThreshold) setBulkThreshold(cfg.bulkConfirmationThreshold);
                if (cfg?.maxAttachmentSizeBytes) setMaxAttachmentMb(cfg.maxAttachmentSizeBytes / (1024 * 1024));
            })
            .catch(() => {
                /* fall back to defaults */
            });
    }, []);

    const handleAttachmentChange = (e) => {
        const files = Array.from(e.target.files || []);
        const tooLarge = files.filter((f) => f.size > maxAttachmentMb * 1024 * 1024);
        if (tooLarge.length > 0) {
            window.alert(`These files exceed the ${maxAttachmentMb}MB limit: ${tooLarge.map((f) => f.name).join(', ')}`);
        }
        setAttachments(files.filter((f) => f.size <= maxAttachmentMb * 1024 * 1024));
    };

    const buildOptions = () => ({
        sendMode,
        scheduledAt: sendMode === 'SCHEDULED' ? scheduledAt : null,
        priority,
        replyTo: replyTo || null,
        cc: cc || null,
        bcc: bcc || null,
        attachments,
    });

    const handlePrimaryAction = async () => {
        if (sendMode === 'DRAFT') {
            onSaveDraft?.(buildOptions());
            return;
        }

        const isBulk = recipientCount > bulkThreshold;
        const baseConfirmed = await confirm({
            title: sendMode === 'SCHEDULED' ? 'Schedule this email?' : 'Send this email now?',
            message: `This will ${sendMode === 'SCHEDULED' ? 'schedule delivery to' : 'send to'} ${recipientCount} recipient${recipientCount === 1 ? '' : 's'}. Continue?`,
            confirmLabel: sendMode === 'SCHEDULED' ? 'Schedule' : 'Send',
            type: 'primary',
        });
        if (!baseConfirmed) return;

        if (isBulk) {
            const bulkConfirmed = await confirm({
                title: 'Confirm bulk send',
                message: `You are about to email ${recipientCount} recipients, which is above the ${bulkThreshold}-recipient threshold for extra confirmation. Are you absolutely sure you want to proceed?`,
                confirmLabel: 'Yes, send to all',
                type: 'danger',
            });
            if (!bulkConfirmed) return;
        }

        onSend?.(buildOptions());
    };

    return (
        <div className="admin-card mb-3">
            <h5 className="mb-3"><i className="bi bi-gear me-2"></i>Delivery Options</h5>

            <div className="mb-3">
                <div className="email-sendmode-group" role="radiogroup" aria-label="Send timing">
                    {[
                        { value: 'IMMEDIATE', label: 'Send now', icon: 'bi-send' },
                        { value: 'SCHEDULED', label: 'Schedule', icon: 'bi-clock-history' },
                        { value: 'DRAFT', label: 'Save draft', icon: 'bi-file-earmark' },
                    ].map((opt) => (
                        <label
                            key={opt.value}
                            className={`email-sendmode-option${sendMode === opt.value ? ' checked' : ''}`}
                        >
                            <input
                                type="radio"
                                name="sendMode"
                                className="visually-hidden"
                                checked={sendMode === opt.value}
                                onChange={() => setSendMode(opt.value)}
                            />
                            <i className={`bi ${opt.icon} me-1`}></i>
                            {opt.label}
                        </label>
                    ))}
                </div>
                {sendMode === 'SCHEDULED' && (
                    <input
                        type="datetime-local"
                        className="form-control form-control-sm mt-2"
                        value={scheduledAt}
                        onChange={(e) => setScheduledAt(e.target.value)}
                    />
                )}
            </div>

            <div className="mb-3 form-check form-switch">
                <input className="form-check-input" type="checkbox" id="priority-high" checked={priority === 'HIGH'} onChange={(e) => setPriority(e.target.checked ? 'HIGH' : 'NORMAL')} />
                <label className="form-check-label" htmlFor="priority-high">
                    <i className="bi bi-exclamation-circle me-1 text-muted"></i>High priority
                </label>
            </div>

            <div className="mb-2">
                <label className="form-label small">Reply-To</label>
                <input type="email" className="form-control form-control-sm" value={replyTo} onChange={(e) => setReplyTo(e.target.value)} />
            </div>
            <div className="row g-2 mb-3">
                <div className="col-6">
                    <label className="form-label small">CC</label>
                    <input type="text" className="form-control form-control-sm" value={cc} onChange={(e) => setCc(e.target.value)} placeholder="comma-separated" />
                </div>
                <div className="col-6">
                    <label className="form-label small">BCC</label>
                    <input type="text" className="form-control form-control-sm" value={bcc} onChange={(e) => setBcc(e.target.value)} placeholder="comma-separated" />
                </div>
            </div>

            <div className="mb-3">
                <label className="form-label small">Attachments (max {maxAttachmentMb}MB each)</label>
                <input type="file" multiple className="form-control form-control-sm" onChange={handleAttachmentChange} />
                {attachments.length > 0 && (
                    <ul className="small text-muted mt-1 mb-0">
                        {attachments.map((f) => <li key={f.name}>{f.name}</li>)}
                    </ul>
                )}
            </div>

            {recipientCount > bulkThreshold && sendMode !== 'DRAFT' && (
                <div className="alert alert-warning py-2 small">
                    <i className="bi bi-exclamation-triangle me-1"></i>
                    This send targets {recipientCount} recipients, above the {bulkThreshold}-recipient threshold — an extra confirmation step will be required.
                </div>
            )}

            <div className="d-flex gap-2">
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => onSendTest?.(buildOptions())} disabled={sending}>
                    <i className="bi bi-send-check me-1"></i> Send Test Email
                </button>
                <button type="button" className="btn btn-primary btn-sm ms-auto" onClick={handlePrimaryAction} disabled={sending}>
                    {sending ? 'Working…' : sendMode === 'DRAFT' ? 'Save Draft' : sendMode === 'SCHEDULED' ? 'Schedule' : 'Send'}
                </button>
            </div>
        </div>
    );
};

export default DeliveryOptionsPanel;
